import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { searchTopicsBySearchTerm } from '../../api/topic';
import { RootState } from '../../store';
import SearchResultsItem from './SearchResultsItem';
import TopicSidebarUtils from './util';

import './SearchResults.css';
import { ITopicDisplay } from './interfaces';

interface SearchResultTopicWithRelevantData {
    topic: ITopicDisplay;
    projectName: string;
    color: string;
    relevantMessage?: string;
    isArchived?: boolean;
}

interface SearchResultsProps {
    searchTerm: string;
    enterTopicCallback: (topic: ITopicDisplay) => void;
}

const SearchResults = ({
    searchTerm,
    enterTopicCallback,
}: SearchResultsProps) => {
    const [searchResults, setSearchResults] = useState<
        SearchResultTopicWithRelevantData[]
    >([]);

    const localization = useSelector(
        (state: RootState) =>
            state.localization.localizationStrings.susy.project.projectTopic
                .sidebar.search
    );

    useEffect(() => {
        if (!searchTerm) {
            setSearchResults([]);
            return;
        }

        const debounceTimeout = setTimeout(getSearchResultsBySearchTerm, 500);

        return () => clearTimeout(debounceTimeout);
    }, [searchTerm]);

    const getSearchResultsBySearchTerm = async () => {
        const searchResultsFromApi = await searchTopicsBySearchTerm(searchTerm);

        const topics = searchResultsFromApi.topics;
        const archivedTopics = searchResultsFromApi.archivedTopics;

        const colorOfDisabled = 'var(--susy-neutral-color-80)';
        const parsedTopics: SearchResultTopicWithRelevantData[] = [
            ...topics.map((sr) => {
                return {
                    topic: sr,
                    projectName: sr.project.title,
                    color: TopicSidebarUtils.getCalculatedTopicColor(
                        sr.project.title
                    ),
                    relevantMessage: sr.relevantMessage?.text,
                };
            }),
            ...archivedTopics.map((sr) => {
                return {
                    topic: { ...sr, isArchived: true },
                    projectName: sr.project.title,
                    color: colorOfDisabled,
                    relevantMessage: sr.relevantMessage?.text,
                    isArchived: sr.isArchived,
                };
            }),
        ];

        setSearchResults(parsedTopics);
    };

    return (
        <div
            className='search-results-container'
            data-testid='search-results-container'
        >
            {searchResults.length === 0 && (
                <h4>{localization.noResultsFound}</h4>
            )}
            {searchResults.length > 0 &&
                searchResults.map((sr) => (
                    <SearchResultsItem
                        key={sr.topic._id}
                        topic={sr.topic}
                        projectName={sr.projectName}
                        searchTerm={searchTerm}
                        relevantMessage={sr.relevantMessage}
                        color={sr.color}
                        enterTopicCallback={enterTopicCallback}
                    />
                ))}
        </div>
    );
};

export default SearchResults;
